/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.kiloparsec

import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import net.sergeych.bintools.toDataSource
import net.sergeych.bipack.BipackDecoder
import net.sergeych.bipack.BipackEncoder
import net.sergeych.kiloparsec.Command.Companion.unpackCall
import net.sergeych.utools.unpack

/**
 * Typesafe command definition. Command is a universal entity in kiloparsec (and also diwan): it is used
 * in node-2-node protocols and client API, and most importantly in calling smart contract
 * methods. This is essentially a Kotlin binding to typesafe serialize command calls and
 * deserialize results.
 *
 * To create command instances, it is recommended to use [command] that returns [CommandDelegate].
 *
 * Use [packCall] to serialize the command call with some arguments.
 *
 * Note that `Command` instances themselves are not serialized, instead, the call is serialized,
 * in the form of [Call], containing name and properly serialized arguments.
 *
 * [unpackCall] deserialized result of the [packCall] so the proper handler for the command could
 * be used. Then the result of the execution could be packed with [exec] and then unpacked with
 * [unpackResult].
 *
 */
open class Command<A, R>(
    val name: String,
    val argsSerializer: KSerializer<A>,
    val resultSerializer: KSerializer<R>
) {
    @Serializable
    data class Call(val name: String,val serializedArgs: UByteArray)

    /**
     * Pack command invocation with specified arguments.
     */
    fun packCall(args: A): UByteArray = BipackEncoder.encode(createCall(args)).toUByteArray()

    /**
     * Create [Call] instance for specified args vy serializing it properly
     */
    fun createCall(args: A): Call = Call(name, BipackEncoder.encode(argsSerializer, args).toUByteArray())

    /**
     * Unpack result, obtained by [exec].
     */
    fun unpackResult(packedResult: UByteArray): R =
        unpack(resultSerializer, packedResult)

    /**
     * Execute a command unpacking args.
     *
     * @param packedArgs arguments, as provided by [packCall] in the [Call] instance
     * @param handler actual code to execute the command
     * @return properly serialized result to be unpacked with [unpackResult].
     */
    suspend fun exec(packedArgs: UByteArray, handler: suspend (A) -> R): UByteArray =
        BipackEncoder.encode(
            resultSerializer,
            handler(
                BipackDecoder.decode(packedArgs.toDataSource(), argsSerializer))
        ).toUByteArray()

    companion object {
        /**
         * Unpack command invocation instance from [packCall]. Use [exec] to deserialize arguments and
         * perform command.
         */
        fun unpackCall(packedCall: UByteArray): Call = BipackDecoder.decode(packedCall.toDataSource())
    }
}